import { consumer } from '@tock/coretypes';
import ImgixClient from '@imgix/js-core';
import {
  legacyImageUrl,
  legacyCropImageUrl,
  legacyCenterCropThumbnailUrl,
  legacyCenterCropThumbnailUrlSquare,
  legacyGuestImageUrl,
} from './legacyImage';
import {
  IMGIX_DOMAIN,
  DPPX_SIZES,
  hasImgIxUrl,
  hasLegacyCdnUrl,
  getRelativeCloudStoragePath,
  getRelativeImgixUrl,
  getImgixBasePath,
  isUrlEncoded,
  type SizeOptions,
  type ImageOptions,
} from './imgix';
import {
  centerCropUrl as cdaasCenterCropUrl,
  getAutoFormatUrl as cdaasGetAutoFormatUrl,
  hasCdaasUrl,
} from './cdaas';
import { logLegacyImageUrl } from './legacyImageLogger';

// Using an empty src attribute on an image tag in html can be dangerous because it causes the
// browser to issue another request for the current page. This can cause a thundering herd problem.
// Instead, in the unusual situation where we can't generate a valid image url, we fall back to
// a 1x1 transparent gif as a data url.
const PLACEHOLDER_GIF = 'data:image/gif;base64,R0lGODlhAQABAID/AMDAwAAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

const client = new ImgixClient({
  domain: IMGIX_DOMAIN,
});



/**
 * Extracts existing query parameters from an imgix URL
 * Example: https://tock-public.imgix.net/path/to/image.jpg?w=100&h=200 -> { w: '100', h: '200' }
 *
 * Note: Normalizes width→w and height→h to use imgix's standard parameter names.
 */
function getExistingImgixParams(url: string): Record<string, string | number> {
  const relativeUrl = getRelativeImgixUrl(url);
  const questionMarkIndex = relativeUrl.indexOf('?');

  if (questionMarkIndex < 0) {
    return {};
  }

  const queryString = relativeUrl.substring(questionMarkIndex + 1);
  const params: Record<string, string | number> = {};

  if (queryString) {
    queryString.split('&').forEach(pair => {
      const [key, value] = pair.split('=');
      if (key) {
        const decodedKey = decodeURIComponent(key);
        const decodedValue = decodeURIComponent(value || '');

        // Normalize width/height to w/h (imgix's standard parameter names)
        const normalizedKey = decodedKey === 'width' ? 'w' :
                              decodedKey === 'height' ? 'h' :
                              decodedKey;

        params[normalizedKey] = decodedValue;
      }
    });
  }

  return params;
}

/**
 * Unified image URL builder that handles:
 * - Legacy Google Cloud URLs
 * - Cloud Storage URLs
 * - Already-processed imgix URLs
 *
 * When the URL is already an imgix URL, this will rebuild it with new parameters,
 * preserving existing parameters that are not being overridden.
 * This matches the behavior of the Java CdnImageUrlGenerator.
 */
export function imageUrl(url?: string, options?: ImageOptions): string {
  if (!url) {
    return PLACEHOLDER_GIF;
  } else if (hasCdaasUrl(url)) {
    return cdaasGetAutoFormatUrl(url, options);
  } else if (hasLegacyCdnUrl(url)) {
    logLegacyImageUrl('imageUrl', url);
    return legacyImageUrl(url, options?.width);
  }

  // For imgix URLs, extract the base path and merge parameters
  const isImgixUrl = hasImgIxUrl(url);
  const src = isImgixUrl ? getImgixBasePath(url) : getRelativeCloudStoragePath(url);

  // Merge existing parameters with new ones (new parameters override existing)
  const existingParams = isImgixUrl ? getExistingImgixParams(url) : {};
  // Convert width/height to w/h for imgix API
  const normalizedOptions: Record<string, string | number> = {};
  if (options?.width !== undefined) normalizedOptions.w = options.width;
  if (options?.height !== undefined) normalizedOptions.h = options.height;
  if (options?.q !== undefined) normalizedOptions.q = options.q;

  const mergedParams = { ...existingParams, ...normalizedOptions, auto: 'format' };

  return client.buildURL(
    src,
    mergedParams,
    { disablePathEncoding: isUrlEncoded(url) }
  );
}

/**
 * Creates a center-cropped image URL with specified dimensions.
 * Handles both legacy and imgix URLs, and supports modifying already-processed imgix URLs.
 * Preserves existing parameters that are not being overridden.
 */
export function cropImageUrl(url: string | undefined, width: number, height: number, quality?: number): string {
  if (!url) {
    return PLACEHOLDER_GIF;
  } else if (hasCdaasUrl(url)) {
    return cdaasCenterCropUrl(url, { width, height, q: quality });
  } else if (hasLegacyCdnUrl(url)) {
    logLegacyImageUrl('cropImageUrl', url);
    return legacyCropImageUrl(url, width, height);
  }

  // For imgix URLs (including already-processed ones), extract base path and merge parameters
  const isImgixUrl = hasImgIxUrl(url);
  const src = isImgixUrl ? getImgixBasePath(url) : getRelativeCloudStoragePath(url);

  // Merge existing parameters with new ones (use w/h for imgix API)
  const existingParams = isImgixUrl ? getExistingImgixParams(url) : {};
  const newParams = {
    auto: 'format',
    fit: 'crop',
    crop: 'focalpoint',
    h: height,
    w: width,
    ...(quality !== undefined && { q: quality }),
  };
  const mergedParams = { ...existingParams, ...newParams };

  return client.buildURL(
    src,
    mergedParams,
    { disablePathEncoding: isUrlEncoded(url) }
  );
}

/**
 * Creates a standard-sized thumbnail (576x384) with center crop.
 * Supports modification of already-processed imgix URLs.
 */
export function centerCropThumbnailUrl(imageUrl: string | undefined): string {
  if (!imageUrl) {
    return PLACEHOLDER_GIF;
  }

  if (hasLegacyCdnUrl(imageUrl)) {
    logLegacyImageUrl('centerCropThumbnailUrl', imageUrl);
    return legacyCenterCropThumbnailUrl(imageUrl);
  }

  return cropImageUrl(imageUrl, 576, 384, 35);
}

/**
 * Creates a square thumbnail (100x100) with center crop.
 * Supports modification of already-processed imgix URLs.
 */
export function centerCropThumbnailUrlSquare(imageUrl: string | undefined): string {
  if (!imageUrl) {
    return PLACEHOLDER_GIF;
  }

  if (hasLegacyCdnUrl(imageUrl)) {
    logLegacyImageUrl('centerCropThumbnailUrlSquare', imageUrl);
    return legacyCenterCropThumbnailUrlSquare(imageUrl);
  }

  return cropImageUrl(imageUrl, 100, 100);
}

/**
 * Creates a guest/avatar image URL.
 * Supports modification of already-processed imgix URLs.
 */
export function guestImageUrl(imageUrl: string | undefined, size = 100): string {
  if (!imageUrl) {
    return PLACEHOLDER_GIF;
  }

  if (hasCdaasUrl(imageUrl) || hasImgIxUrl(imageUrl)) {
    return cropImageUrl(imageUrl, size, size);
  }

  return legacyGuestImageUrl(imageUrl, size);
}

/**
 * Generates a srcset string for responsive images at different pixel densities.
 * Supports modification of already-processed imgix URLs.
 */
function getAutoFormatSrcSetForWidthPx(width: number, url?: string): string {
  if (!url) {
    return PLACEHOLDER_GIF;
  }

  return DPPX_SIZES.map(
    (size) => `${imageUrl(url, { width: width * size })} ${size}x`
  ).join(', ');
}

/**
 * Generates both src and srcSet for responsive images.
 * Supports modification of already-processed imgix URLs.
 */
export function getAutoFormatSrcAndSrcSetForWidthPx(
  { width }: { width: number },
  url?: string
): { src: string; srcSet: string } {
  if (!url) {
    return { src: PLACEHOLDER_GIF, srcSet: PLACEHOLDER_GIF };
  }

  return {
    src: imageUrl(url, { width }),
    srcSet: getAutoFormatSrcSetForWidthPx(width, url),
  };
}

/**
 * Gets a thumbnail URL for a business profile image.
 * Supports modification of already-processed imgix URLs.
 */
export const getBusinessThumbnailSrc = (
  business: consumer.IBusinessConfig | consumer.OfferingBusiness,
  options: SizeOptions = { width: 120, height: 120 }
): string => {
  if (!business) {
    return PLACEHOLDER_GIF;
  }
  const [firstProfileImage] = (business.profileImage ?? []).map((image: any) => image.imageBackingUrl);
  return firstProfileImage
    ? cropImageUrl(firstProfileImage, options.width, options.height)
    : PLACEHOLDER_GIF;
};

export default client;
